import { GateMovementState2 as State } from '@/__generated__/graphql';
import { useFormatters } from '@/hooks/useFormatters';
import TimeElapsed from '@components/TimeElapsed';
import { MovementDataRenderer } from '@features/gateCommon';
import { formatPosition } from '@features/gateCommon/utils/formatPosition';
import { stateTone, type MovementTone } from '@features/gateCommon/utils/gateMovementState';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Skeleton } from '@mui/material';
import { FC, ReactNode } from 'react';

interface Props {
  movementId: number | null;
  /**
   * El valor de posición es una **superficie con texto encima**, así que lo que sale de acá es el
   * nombre del tono y no un color: el par relleno/tinta lo resuelve `auToneSurfaceSx` contra
   * `AU_TINT`, medido a 4,5:1. El canal de marca gráfica —`getStateColor`, a 3:1— es el de la barra
   * de progreso, y mezclarlos es el bug que separar los dos canales viene a evitar.
   */
  render: FC<{
    label: string;
    position: ReactNode;
    relativeTime: ReactNode;
    tone: MovementTone;
  }>;
}

/** Mientras el movimiento carga no hay estado todavía: el acromático, igual que las tres etapas
 *  en vuelo. */
const defaultTone = stateTone(State.InQueue);

export const OperationPositionData: FC<Props> = ({ movementId, render }) => {
  const { formatMetricValue } = useFormatters();
  const { i18n } = useLingui();

  return movementId ? (
    <MovementDataRenderer
      movementId={movementId}
      render={({ movement, gate }) => {
        const finalizedStates = [State.Finalized, State.FinalizedWithError];

        let label = t(i18n)`Posición actual`;
        let position: number | null = movement.positions[0]?.[1] ?? null;
        let timestamp: number | null = movement.positions[0]?.[0] ?? null;

        // La telemetría continua de la compuerta (`useGatePositionSync`) está desactivada
        // pendiente AMARU-866: este valor es `lastKnownPosition` (último mensaje o Influx),
        // refrescado solo por evento MQTT o refetch de visibilidad — no un stream continuo.
        if (!finalizedStates.includes(movement.state)) {
          const telemetryPosition = gate.movements2?.lastKnownPosition;
          if (telemetryPosition) {
            position = telemetryPosition.point;
            timestamp = telemetryPosition.timestamp;
          }
        }

        if (finalizedStates.includes(movement.state)) {
          label = t(i18n)`Posición final`;
          position = movement.positions[0]?.[1] ?? null;
          timestamp = movement.positions[0]?.[0] ?? null;
        }

        return render({
          label,
          position:
            position !== null ? (
              formatPosition(position, formatMetricValue)
            ) : (
              <Trans>Sin datos</Trans>
            ),
          relativeTime: timestamp ? (
            <TimeElapsed timestamp={timestamp} />
          ) : (
            <Trans>Sin datos</Trans>
          ),
          tone: stateTone(movement.state),
        });
      }}
      fallback={() =>
        render({
          label: t(i18n)`Posición actual`,
          // Sin texto adentro: MUI oculta los hijos de un `Skeleton` con `& > *`, que no matchea un
          // nodo de texto pelado, así que se leía «xx cm» debajo de la barra. El ancho reserva el
          // del valor para que la caja no cambie de tamaño al llegar el dato.
          position: <Skeleton variant="text" width={56} sx={{ mx: 'auto' }} />,
          relativeTime: <Skeleton variant="text" width={84} sx={{ mx: 'auto' }} />,
          tone: defaultTone,
        })
      }
    />
  ) : (
    <Trans>Iniciando comunicación</Trans>
  );
};
